import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useIntroComplete } from '../context/IntroContext';
import { PATHS } from '../navigation/paths';
import { COLORS, FONTS, SPACING, TYPO } from '../theme/designTokens';
import { MOTION_EASE } from './motion/easing';
import {
  COOKIE_SETTINGS_EVENT,
  CookieConsent,
  getCookieConsent,
  setCookieConsent,
} from '../utils/cookieConsent';
import { trackPageView } from '../analytics/googleAnalytics';

const MotionBox = motion.create(Box);

const bannerActionSx = {
  fontFamily: FONTS.sans,
  fontWeight: 400,
  color: COLORS.text,
  textTransform: 'none' as const,
  boxShadow: 'none',
  backgroundColor: 'transparent',
  borderRadius: 0,
  whiteSpace: 'nowrap' as const,
  fontSize: { xs: '12px', md: '14px' },
  lineHeight: 1.4,
  letterSpacing: { xs: '0.02em', md: '0.42px' },
  width: { xs: 'auto', md: 150 },
  minWidth: { xs: 0, md: 150 },
  px: { xs: 0, md: '25px' },
  py: { xs: '2px', md: '15px' },
  border: { xs: 'none', md: `1px solid ${COLORS.btnBorderLight}` },
  textDecoration: { xs: 'underline', md: 'none' },
  textUnderlineOffset: '3px',
  '&:hover': {
    backgroundColor: { xs: 'transparent', md: COLORS.bg },
    border: { xs: 'none', md: `1px solid ${COLORS.bg}` },
    color: { xs: COLORS.textMuted, md: COLORS.textOnDark },
    boxShadow: 'none',
  },
};

const CookieBanner: React.FC = () => {
  const introComplete = useIntroComplete();
  const reduceMotion = useReducedMotion();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!introComplete || getCookieConsent() !== null) return;
    const show = () => setVisible(true);
    if (document.documentElement.classList.contains('intro-done')) {
      show();
      return;
    }
    const observer = new MutationObserver(() => {
      if (!document.documentElement.classList.contains('intro-done')) return;
      observer.disconnect();
      show();
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [introComplete]);

  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener(COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, open);
  }, []);

  const dismiss = useCallback((consent: CookieConsent) => {
    setCookieConsent(consent);
    if (consent === 'accepted') {
      trackPageView(`${window.location.pathname}${window.location.search}`);
    }
    setVisible(false);
  }, []);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {visible && (
        <MotionBox
          role="dialog"
          aria-live="polite"
          aria-label="Cookie-Hinweis"
          initial={reduceMotion ? false : { y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={reduceMotion ? undefined : { y: '100%', opacity: 0 }}
          transition={{ duration: 0.55, ease: MOTION_EASE }}
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: COLORS.bgWhite,
            borderTop: `1px solid ${COLORS.btnBorderLight}`,
            boxShadow: '0 -8px 32px rgba(20, 28, 44, 0.08)',
          }}
        >
          <Box
            sx={{
              maxWidth: SPACING.maxContent,
              mx: 'auto',
              px: { xs: '16px', md: '71px' },
              py: { xs: '14px', md: '20px' },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              gap: { xs: '10px', md: '32px' },
            }}
          >
            <Typography
              sx={{
                ...TYPO.bodyLight,
                fontSize: { xs: '12px', md: '15px' },
                lineHeight: { xs: 1.45, md: 1.35 },
                width: '100%',
                maxWidth: { md: 720 },
              }}
            >
              Wir verwenden Cookies, um die Nutzung unserer Website zu analysieren und zu
              verbessern. Weitere Informationen finden Sie in unserer{' '}
              <Box
                component={Link}
                to={PATHS.datenschutz}
                sx={{
                  color: COLORS.text,
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                  '&:hover': { color: COLORS.textMuted },
                }}
              >
                Datenschutzerklärung
              </Box>
              .
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: { xs: '18px', md: '8px' },
                flexShrink: 0,
              }}
            >
              <Button
                disableRipple
                sx={bannerActionSx}
                onClick={() => dismiss('declined')}
              >
                Ablehnen
              </Button>
              <Button
                disableRipple
                sx={bannerActionSx}
                onClick={() => dismiss('accepted')}
              >
                Akzeptieren
              </Button>
            </Box>
          </Box>
        </MotionBox>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default CookieBanner;
